import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { AddedOrderLines } from './AddedOrderLines';
import { api } from '../../services/api.js';
import type { Distributor, Product, ProductVariant } from '../../types';
import { SearchableCustomerSelect } from '../visits/SearchableCustomerSelect';
import { ProductVariantSelects } from './ProductVariantSelects';
import { extractApiError } from '../../utils/pdfDownload';
import { computePromoSplit } from '../../utils/promotion';
import { formatDocumentToast } from '../../utils/documentToast';
import {
  defaultOrderDistributorId,
  sortDistributorsForOrderCapture,
} from '../../utils/orderDistributorSort';
import { catalogCasePrice, isSupermarketStoreType, lehUnitPrice } from '../../utils/casePrice';
interface NewOrderModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialPosId?: string;
}
type PromotionType = '1+1' | '2+1' | '3+1' | '4+1' | 'free_samples' | 'none';
interface OrderLine {
  id: string;
  product_id: string;
  product_name: string;
  variant_id?: string;
  variant_label: string;
  quantity: number;
  distributor_id: string;
  distributor_name: string;
  promotion_type: PromotionType;
  unit_price_override?: number;
  unit_price: number;
  notes?: string;
  promo_paid: number;
  promo_free: number;
  promo_delivered: number;
  net_total: number;
  pfand_total: number;
  gross_total: number;
}
const PROMOTION_OPTIONS: { value: PromotionType; label: string }[] = [
  { value: 'none', label: 'No Promo' },
  { value: '1+1', label: '1+1 Free' },
  { value: '2+1', label: '2+1 Free' },
  { value: '3+1', label: '3+1 Free' },
  { value: '4+1', label: '4+1 Free' },
  { value: 'free_samples', label: 'Free Samples' },
];
const parsePositiveInt = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed;
};
const parseOptionalPrice = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return parsed;
};
const formatMoney = (value: number) => `${value.toFixed(2)} EUR`;
const formatVariantLabel = (variant: ProductVariant) => {
  const volume = variant.unit_volume ? ` ${variant.unit_volume}` : '';
  return `${variant.gebinde_name} (${variant.units_per_case}x${volume})`;
};
export const NewOrderModal: React.FC<NewOrderModalProps> = ({
  open,
  onClose,
  onCreated,
  initialPosId,
}) => {
  const controlClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500';
  const [products, setProducts] = useState<Product[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [defaultDistributorId, setDefaultDistributorId] = useState('');
  const [posId, setPosId] = useState('');
  const [posStoreType, setPosStoreType] = useState('');
  const [productId, setProductId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [distributorId, setDistributorId] = useState('');
  const [quantityInput, setQuantityInput] = useState<string>('1');
  const [unitPriceOverride, setUnitPriceOverride] = useState<string>('');
  const [promotionType, setPromotionType] = useState<PromotionType>('none');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!open) return;
    resetForm();
    void loadStaticData();
  }, [open]);
  useEffect(() => {
    if (!productId) {
      setVariants([]);
      setVariantId('');
      return;
    }
    void loadVariants(productId);
  }, [productId]);
  const resetLineEditor = (nextDistributorId: string) => {
    setProductId('');
    setVariantId('');
    setVariants([]);
    setQuantityInput('1');
    setUnitPriceOverride('');
    setPromotionType('none');
    setDistributorId(nextDistributorId);
    setNotes('');
  };
  const resetForm = () => {
    setPosId('');
    setPosStoreType('');
    setLines([]);
    resetLineEditor(defaultDistributorId);
  };
  const loadStaticData = async () => {
    try {
      const [prodRes, distRes] = await Promise.all([
        api.get('/products?is_active=true'),
        api.get('/distributors'),
      ]);
      const distributorList = sortDistributorsForOrderCapture(distRes.data as Distributor[]);
      const selfDistributorId = defaultOrderDistributorId(distributorList);
      setProducts(prodRes.data);
      setDistributors(distributorList);
      setDefaultDistributorId(selfDistributorId);
      setDistributorId(selfDistributorId);
    } catch {
      toast.error('Failed to load form data');
    }
  };
  const loadVariants = async (pid: string) => {
    try {
      const res = await api.get(`/products/${pid}/variants`);
      const nextVariants = res.data as ProductVariant[];
      setVariants(nextVariants);
      if (nextVariants.length === 1) setVariantId(nextVariants[0].id);
      else setVariantId('');
    } catch {
      toast.error('Failed to load product variants');
    }
  };
  const selectedProduct = products.find((item) => item.id === productId);
  const selectedVariant = variants.find((item) => item.id === variantId);
  const selectedDistributor = distributors.find((item) => item.id === distributorId);
  const parsedQuantity = parsePositiveInt(quantityInput);
  const hasInvalidQuantity = quantityInput.trim() !== '' && parsedQuantity === null;
  const parsedOverride = parseOptionalPrice(unitPriceOverride);
  const hasInvalidOverride = unitPriceOverride.trim() !== '' && parsedOverride === null;
  const effectivePrice = selectedVariant
    ? catalogCasePrice(selectedVariant, posStoreType, parsedOverride)
    : (parsedOverride ?? 0);
  const selectedLehUnit = selectedVariant
    ? lehUnitPrice(selectedVariant.leh_unit_price_net)
    : null;
  const previewPromo = computePromoSplit(parsedQuantity ?? 0, promotionType);
  const previewNet = effectivePrice * previewPromo.paid;
  const previewPfand = Number(selectedVariant?.pfand_per_case ?? 0) * previewPromo.delivered;
  const requiresVariant = variants.length > 0;
  const canAddLine = Boolean(
    productId &&
      distributorId &&
      parsedQuantity &&
      !hasInvalidOverride &&
      (!requiresVariant || variantId)
  );
  const grandTotals = useMemo(
    () =>
      lines.reduce(
        (acc, line) => ({
          net: acc.net + line.net_total,
          pfand: acc.pfand + line.pfand_total,
          gross: acc.gross + line.gross_total,
        }),
        { net: 0, pfand: 0, gross: 0 }
      ),
    [lines]
  );
  const handleAddLine = () => {
    if (!selectedProduct) {
      toast.error('Choose a product before adding');
      return;
    }
    if (!parsedQuantity) {
      toast.error('Quantity must be at least 1');
      return;
    }
    if (!selectedDistributor) {
      toast.error('Choose a distributor before adding');
      return;
    }
    if (requiresVariant && !selectedVariant) {
      toast.error('Choose a Gebinde variant before adding');
      return;
    }
    if (hasInvalidOverride) {
      toast.error('Price override must be a number >= 0');
      return;
    }
    const promo = computePromoSplit(parsedQuantity, promotionType);
    const unitPrice = effectivePrice;
    const pfandPerCase = Number(selectedVariant?.pfand_per_case ?? 0);
    const lineNet = unitPrice * promo.paid;
    const linePfand = pfandPerCase * promo.delivered;
    const nextLine: OrderLine = {
      id: `${Date.now()}-${Math.random()}`,
      product_id: selectedProduct.id,
      product_name: `${selectedProduct.manufacturer_name || ''} ${selectedProduct.name}`.trim(),
      variant_id: selectedVariant?.id,
      variant_label: selectedVariant ? formatVariantLabel(selectedVariant) : 'No variant',
      quantity: parsedQuantity,
      distributor_id: selectedDistributor.id,
      distributor_name: selectedDistributor.name,
      promotion_type: promotionType,
      unit_price_override: parsedOverride ?? undefined,
      unit_price: unitPrice,
      notes: notes.trim() || undefined,
      promo_paid: promo.paid,
      promo_free: promo.free,
      promo_delivered: promo.delivered,
      net_total: lineNet,
      pfand_total: linePfand,
      gross_total: lineNet + linePfand,
    };
    setLines((prev) => [...prev, nextLine]);
    resetLineEditor(defaultDistributorId || distributorId);
  };
  const handleRemoveLine = (lineId: string) => {
    setLines((prev) => prev.filter((line) => line.id !== lineId));
  };
  const handleSubmit = async () => {
    if (submitting) return;
    if (!posId) {
      toast.error('Choose a PoS first');
      return;
    }
    if (lines.length === 0) {
      toast.error('Add at least one line before creating orders');
      return;
    }
    const payload = lines.map((line) => {
      const body: Record<string, unknown> = {
        pos_id: posId,
        product_id: line.product_id,
        quantity: line.quantity,
        distributor_id: line.distributor_id,
        promotion_type: line.promotion_type,
        source: 'manual',
      };
      if (line.variant_id) body.variant_id = line.variant_id;
      if (line.unit_price_override !== undefined) body.unit_price_override = line.unit_price_override;
      if (line.notes) body.notes = line.notes;
      return body;
    });
    setSubmitting(true);
    try {
      const res = await api.post('/orders/bulk', payload);
      const documents = res.data?.documents || [];
      toast.success(
        formatDocumentToast(documents) ||
          `${lines.length} order${lines.length > 1 ? 's' : ''} added to Own delivery`
      );
      onCreated();
      onClose();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Create failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">New order line</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <SearchableCustomerSelect
            onSelect={(p) => {
              setPosId(p.id);
              setPosStoreType(p.store_type);
            }}
            onClear={() => {
              setPosId('');
              setPosStoreType('');
            }}
            initialPosId={initialPosId}
            locked={Boolean(initialPosId) && Boolean(posId)}
          />

          <div className="rounded-xl border border-gray-200 p-4 space-y-4">
            <h3 className="text-sm font-bold text-gray-900">Line editor</h3>

            <ProductVariantSelects
              products={products}
              variants={variants}
              productId={productId}
              variantId={variantId}
              onProductChange={setProductId}
              onVariantChange={setVariantId}
              formatVariantLabel={(item) =>
                `${formatVariantLabel(item)} - ${formatMoney(catalogCasePrice(item, posStoreType))}`
              }
              controlClass={controlClass}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Quantity (cases) *
                </label>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={quantityInput}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  className={controlClass}
                />
                {hasInvalidQuantity && (
                  <p className="text-xs text-red-600 mt-1">Quantity must be a whole number &ge; 1.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Price override (EUR/case)
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder={
                    selectedVariant
                      ? catalogCasePrice(selectedVariant, posStoreType).toFixed(2)
                      : '0.00'
                  }
                  value={unitPriceOverride}
                  onChange={(e) => setUnitPriceOverride(e.target.value)}
                  className={controlClass}
                />
                {hasInvalidOverride && (
                  <p className="text-xs text-red-600 mt-1">Price override must be &ge; 0.</p>
                )}
                {isSupermarketStoreType(posStoreType) && selectedLehUnit !== null && (
                  <p className="text-xs text-gray-500 mt-1">
                    LEH {selectedLehUnit.toFixed(2)} EUR/unit · {effectivePrice.toFixed(2)} EUR/case
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Promotion</label>
              <select
                value={promotionType}
                onChange={(e) => setPromotionType(e.target.value as PromotionType)}
                className={controlClass}
              >
                {PROMOTION_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Distributor *</label>
              <select value={distributorId} onChange={(e) => setDistributorId(e.target.value)} className={controlClass}>
                {distributors.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.is_self ? ' (Own Delivery)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Own Delivery is the default. This order appears on Orders as a pick list.
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className={controlClass}
              />
            </div>

            {selectedProduct && parsedQuantity && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                {previewPromo.free > 0 && (
                  <div className="flex justify-between bg-yellow-50 -mx-3 -mt-3 mb-2 px-3 py-2 rounded-t-lg border-b border-yellow-200">
                    <span className="text-yellow-800 font-bold">Delivery (incl. promo):</span>
                    <span className="font-bold text-yellow-900">
                      {previewPromo.paid} paid + {previewPromo.free} free = {previewPromo.delivered} cases
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Net total:</span>
                  <span className="font-bold">{formatMoney(previewNet)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pfand:</span>
                  <span className="font-bold">{formatMoney(previewPfand)}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                  <span className="text-gray-900 font-bold">Gross:</span>
                  <span className="font-bold">{formatMoney(previewNet + previewPfand)}</span>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleAddLine}
                disabled={!canAddLine}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Add to order
              </button>
            </div>
          </div>

          <AddedOrderLines lines={lines} onRemove={handleRemoveLine} />

          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Net total:</span>
              <span className="font-bold">{formatMoney(grandTotals.net)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pfand total:</span>
              <span className="font-bold">{formatMoney(grandTotals.pfand)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
              <span className="text-gray-900 font-bold">Gross total:</span>
              <span className="font-bold">{formatMoney(grandTotals.gross)}</span>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 font-bold hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!posId || lines.length === 0 || submitting}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating...' : 'Create order line(s)'}
          </button>
        </div>
      </div>
    </div>
  );
};
